/**
 * Project attachments — sales order, packing slip, design package, photos, recap.
 *
 * Real PM workflow (see docs/sample-files/) is to attach three PDFs per project:
 *   1. Sales order PDF (from SAP) — order #, project ID, customer #, ship-to, line items
 *   2. Packing slip PDF (from warehouse) — delivery #, related ref #, item pull list
 *   3. Design package PDF (multi-page) — cover + floorplan (R1.1) + 3D view + schedule
 *
 * The AI intake pipeline parses these into a structured Project draft.
 */
import { z } from 'zod';
import { IsoDateTimeSchema, UuidSchema } from './common.js';
import { AttachmentKindSchema, DesignPagePartSchema } from './enums.js';
// ----------------------------------------------------------------------------
// Design package page — extracted page info for floorplan reference
// ----------------------------------------------------------------------------
export const DesignPackagePageSchema = z.object({
    page_index: z.number().int().min(0),
    page_label: z
        .string()
        .nullable()
        .describe('e.g. "A0", "R1.1", "R2.1" — extracted from page footer'),
    // Unknown/garbled part labels degrade to "other" rather than failing the
    // whole design-package extraction.
    part: DesignPagePartSchema.catch('other'),
    // Gemini never emits this (it's a blob URL we render later) — allow absent.
    rendered_png_url: z
        .string()
        .url()
        .nullish()
        .describe('Blob URL of high-res PNG render, used as the punch-list canvas'),
    // Optional: a PDF render doesn't always expose pixel dimensions, and the
    // model frequently omits them. Don't reject the whole extraction over it.
    width_px: z.number().int().positive().nullish(),
    height_px: z.number().int().positive().nullish(),
});
// ----------------------------------------------------------------------------
// Attachment — generic file attached to a project
// ----------------------------------------------------------------------------
export const AttachmentSchema = z.object({
    id: UuidSchema,
    project_id: UuidSchema.nullable().describe('null for fleet photos (owned by a vehicle/service-log/issue)'),
    task_id: UuidSchema.nullable().describe('null if attached at project level'),
    punch_item_id: UuidSchema.nullable().describe('non-null for photo evidence pinned to a punch item'),
    note_id: UuidSchema.nullable().describe('non-null when captured with a note — groups media under that note'),
    vehicle_id: UuidSchema.nullable().default(null).describe('non-null for a fleet vehicle photo'),
    service_log_id: UuidSchema.nullable().default(null).describe('non-null for a service-log receipt/photo'),
    vehicle_issue_id: UuidSchema.nullable().default(null).describe('non-null for a vehicle-issue photo'),
    kind: AttachmentKindSchema,
    blob_url: z.string().url(),
    original_filename: z.string().min(1).max(500),
    mime_type: z.string().min(3).max(150),
    byte_size: z.number().int().min(0),
    caption: z.string().nullable(),
    uploaded_by: UuidSchema,
    uploaded_at: IsoDateTimeSchema,
    /** Signed, expiring download URL (audit M2) — what <img>/<a> should use;
     *  the bare /attachments/:id/file route requires auth or this signature. */
    file_url: z.string().optional(),
    // Design-package-only enrichment (populated by the PDF intake pipeline)
    pages: z.array(DesignPackagePageSchema).default([]),
    extracted_metadata: z
        .record(z.unknown())
        .nullable()
        .describe('Free-form structured metadata extracted from the PDF (sap_order, customer #, etc.)'),
});
// ----------------------------------------------------------------------------
// Upload payloads
// ----------------------------------------------------------------------------
export const AttachmentUploadRequestSchema = z.object({
    project_id: UuidSchema,
    task_id: UuidSchema.optional().nullable(),
    kind: AttachmentKindSchema,
    caption: z.string().max(500).optional().nullable(),
    // The file itself comes in via multipart, not JSON
});
// ----------------------------------------------------------------------------
// Sales-order extracted fields (from PDF intake)
// ----------------------------------------------------------------------------
// Extraction is inherently partial — every field is nullish (null OR absent)
// so real-world Gemini output (which omits keys it can't find) validates.
export const SalesOrderExtractSchema = z.object({
    sap_order_number: z.string().nullish(),
    order_date: z.string().nullish(),
    purchase_order_date: z.string().nullish(),
    customer_number: z.string().nullish(),
    sap_project_id: z.string().nullish(),
    ship_to: z
        .object({
        business_name: z.string().nullish(),
        contact_name: z.string().nullish(),
        address_lines: z.array(z.string()).default([]),
        city: z.string().nullish(),
        state: z.string().nullish(),
        postal_code: z.string().nullish(),
        phone: z.string().nullish(),
    })
        .nullish(),
    bill_to: z
        .object({
        business_name: z.string().nullish(),
        address_lines: z.array(z.string()).default([]),
        city: z.string().nullish(),
        state: z.string().nullish(),
        postal_code: z.string().nullish(),
        phone: z.string().nullish(),
    })
        .nullish(),
    line_items: z
        .array(z.object({
        // Coerce: item/SKU numbers come back as numbers as often as strings.
        item_number: z.coerce.string().nullish(),
        product_code: z.string().nullish(),
        description: z.string().nullish(),
        quantity: z.number().nullish(),
        unit: z.string().nullish(),
        unit_price_cents: z.number().int().nullish(),
        total_cents: z.number().int().nullish(),
    }))
        .default([]),
    subtotal_cents: z.number().int().nullish(),
    delivery_install_cents: z
        .number()
        .int()
        .nullish()
        .describe("PM's allocated install budget — KEY"),
    discount_cents: z.number().int().nullish(),
    total_cents: z.number().int().nullish(),
});
// ----------------------------------------------------------------------------
// Packing-slip extracted fields
// ----------------------------------------------------------------------------
export const PackingSlipExtractSchema = z.object({
    delivery_number: z.string().nullish(),
    related_ref_number: z
        .string()
        .nullish()
        .describe('matches sales order number — JOIN KEY'),
    account_number: z.string().nullish(),
    date: z.string().nullish(),
    shipping_method: z.string().nullish().describe('e.g. "Install"'),
    shipping_from: z
        .object({
        address_lines: z.array(z.string()).default([]),
    })
        .nullish(),
    /**
     * The canonical Vari warehouse the "Shipping From" block resolves to, matched
     * against the closed warehouse list at extraction time. Null when it's clearly
     * none of them — the PM then picks one (or "Other") in the review.
     */
    origin_warehouse: z.string().nullish(),
    shipping_to: z
        .object({
        business_name: z.string().nullish(),
        address_lines: z.array(z.string()).default([]),
    })
        .nullish(),
    line_items: z
        .array(z.object({
        item_code: z.string().nullish(),
        description: z.string().nullish(),
        ordered: z.number().nullish(),
        backordered: z.number().nullish(),
        shipped: z.number().nullish(),
    }))
        .default([]),
});
// ----------------------------------------------------------------------------
// Design-package extracted fields (from PDF intake)
// ----------------------------------------------------------------------------
/** One row of a design-package product schedule (new / reused / existing). */
export const DesignScheduleItemSchema = z.object({
    model_number: z.string().nullish(),
    description: z.string(),
    quantity: z.number().int().nullish(),
});
export const DesignPackageExtractSchema = z.object({
    design_pd_number: z.string().nullish(),
    design_job_number: z.string().nullish(),
    revision: z.string().nullish(),
    date: z.string().nullish(),
    client_name: z.string().nullish(),
    site_location: z.string().nullish(),
    interior_designer: z.string().nullish(),
    csm: z.string().nullish(),
    sales_executive: z.string().nullish(),
    // Site-readiness (EX.5) — from the proposed-plan title block + general notes.
    square_footage: z.number().int().nullish(),
    headcount: z.number().int().nullish(),
    readiness_notes: z
        .array(z.string())
        .default([])
        .describe('site-prep / power / electrical / ceiling caveats from general notes'),
    // Product schedules (EX.6) — what's new (install), reused (reconfigure), and
    // existing (leave). New + reused are bolded on the proposed plan.
    new_products: z.array(DesignScheduleItemSchema).default([]),
    reused_products: z.array(DesignScheduleItemSchema).default([]),
    existing_products: z.array(DesignScheduleItemSchema).default([]),
    pages: z.array(DesignPackagePageSchema).default([]),
});
//# sourceMappingURL=attachments.js.map